import { defineStore } from 'pinia'
import type { ILabManagementInvite } from '@team-milva/lab-models/invite'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { orderBy } from 'lodash-es'
import { useAccountStore } from '@/stores/accountStore'
import { useUserStore } from '@/stores/userStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'

export const useInviteStore = defineStore('invite', {
  state: () => ({
    allActiveInvites: [] as ILabManagementInvite[],
    unsubscribeAllActiveInvites: null as (() => void) | null,
  }),

  actions: {
    getAllActiveInvites(): Promise<ILabManagementInvite[]> {
      if (this.allActiveInvitesLoaded) {
        return Promise.resolve(this.allActiveInvites)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'Invites'),
          where('status', '==', 'pending'),
          where('timestamps.expiresAt', '>', Date.now()),
          where('accountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allActiveInvites = querySnapshot.docs.map(
            (doc) => doc.data() as ILabManagementInvite
          )
          this.unsubscribeAllActiveInvites = unsubscribe

          const sortedInvites = orderBy(
            this.allActiveInvites,
            ['email', 'timestamps.createdAt'],
            ['asc', 'desc']
          )
          this.allActiveInvites = sortedInvites

          // Only resolve once
          if (!resolved) {
            resolved = true
            resolve(this.allActiveInvites)
          }
        })
      })
    },

    async cancelInvite(inviteID: string): Promise<void> {
      const result = await managementApi('post', '/invites/revoke', {
        body: {
          inviteID,
        },
      })
      if (!result.success) throw new Error(result.code)
    },

    async resendInvite(invite: ILabManagementInvite): Promise<void> {
      const accountStore = useAccountStore()
      const userStore = useUserStore()

      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)('post', '/invites', {
        body: {
          userID: userStore.currentUser?.id ?? '',
          accountID: accountStore.labAccount?.id ?? '',
          emails: [invite.email],
          mainRole: invite.mainRole,
          workAreas: invite.workAreas,
        },
      })
      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.allActiveInvites = []

      if (this.unsubscribeAllActiveInvites) {
        this.unsubscribeAllActiveInvites()
        this.unsubscribeAllActiveInvites = null
      }
    },
  },

  getters: {
    allActiveInvitesLoaded(state): boolean {
      return state.allActiveInvites.length > 0
    },
  },
})

